
import React from 'react';
import { useCleanInterviewRequest } from '@/hooks/interview/useCleanInterviewRequest';
import { useFormState } from '@/hooks/interview/useFormState';
import { useFormSubmission } from '@/hooks/interview/useFormSubmission';
import InfoBox from './InfoBox';
import PersonalInfoFields from './PersonalInfoFields';
import CleanQuestionDisplay from './CleanQuestionDisplay';
import AnswerSection from './AnswerSection';
import SubmitButton from './SubmitButton';
import ErrorDisplay from './ErrorDisplay';
import LoadingIndicator from './LoadingIndicator';

interface CleanInterviewFormContainerProps {
  requestLink: string;
}

const CleanInterviewFormContainer: React.FC<CleanInterviewFormContainerProps> = ({ 
  requestLink 
}) => {
  const { loading, request, error } = useCleanInterviewRequest({ requestLink });
  
  const {
    answer,
    setAnswer,
    name,
    setName,
    title,
    setTitle,
    company,
    setCompany,
    photoUrl,
    setPhotoUrl,
    wordCount
  } = useFormState();

  const { submitting, handleSubmit: submitForm } = useFormSubmission({ requestLink });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitForm({
      answer,
      name,
      title,
      company,
      photoUrl
    });
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  if (!request) {
    return <ErrorDisplay error="Ingen intervjuforespørsel tilgjengelig" />;
  }

  // All conditions passed, rendering form

  return (
    <div className="bg-white rounded-lg shadow p-8 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Skjema for besvarelse</h1>
      
      <form onSubmit={handleSubmit} id="interview-form" className="space-y-6">
        <InfoBox />
        
        <div className="space-y-4">
          <PersonalInfoFields 
            name={name}
            setName={setName}
            title={title}
            setTitle={setTitle}
            company={company}
            setCompany={setCompany}
            photoUrl={photoUrl}
            setImageUrl={setPhotoUrl}
          />
          
          <CleanQuestionDisplay 
            question={request.question}
            context={request.context}
            contextUrl={request.context_url}
          />
          
          <AnswerSection 
            answer={answer}
            setAnswer={setAnswer}
            wordCount={wordCount}
          />
        </div>

        <SubmitButton submitting={submitting} />
      </form>
    </div>
  );
};

export default CleanInterviewFormContainer;
